import {Component, PanelLifecycleContext} from 'panel';

import {Subscribable} from 'common/types/context/subscribable';
import {
  activatePersistedPylonChat,
  activatePylonChat,
  hidePylonWidget,
  trackPylonLiveChatStarted,
} from 'common/report/external-widgets/pylon';
import {
  hideFrigadeAssistant as hideFrigadeAssistantWidget,
  showFrigadeAssistant as showFrigadeAssistantWidget,
} from 'common/report/external-widgets/frigade';
import {Organization} from 'common/report/queries/app-api/user-metadata-client';
import {LocalStorageKey, Permission, ReportName} from 'common/report/constants';
import {getProjectId, getReportName, getReportUrl} from 'common/report/util';
import {findCurrentValidWorkspaceId, getCurrentWorkspaceId, getFilteredOrgs} from 'iron/routes/global-sidenav/utils';
import {TrackingEvent} from 'iron/routes/global-sidenav/types';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {SendbirdLocalStorageTTL, SendbirdTrackingEvent} from 'iron/routes/global-sidenav/widgets/sendbird-widget/types';
import {trackSparkClosed, trackSparkOpened} from 'common/copilot/tracking';
import {isSparkCopilotEnabled} from 'common/report/util/feature-sampling';
import {isAIEnabled} from 'common/report/util/dashboards/utils';

import {ExpansionMode, ExpansionSource, ExpansionTrigger, PersistedState, Props, SectionKey, State} from './types';

const PersistedStateKey = `v1`;

/**
 * Allows external callsites to control certain functionality within the global sidenav component
 */
export class GlobalSidenavController implements PanelLifecycleContext, Subscribable {
  private connectedComponents: Set<Component<any>> = new Set();
  private subscriptions: Array<() => void> = [];

  declare private props: Props;

  public state: State = {
    showSendbirdWidget: false,
    showFrigadeWidget: false,
    isSearchModalOpen: false,
    isInviteUserModalOpen: false,
    closeInviteModalCallback: undefined,
    focusInputOnOpen: false,
  };

  updateProps(props: Props) {
    const prevProps: Partial<Props> = this.props ?? {};

    if (props.currentUser && props.currentUser !== prevProps.currentUser && props.supportInfo) {
      activatePersistedPylonChat(
        props.currentUser.name,
        props.currentUser.email,
        props.supportInfo.pylonAppId,
        props.supportInfo.pylonEmailHash,
      );
    }

    // if supportInfo arrived after currentUser, fire here.
    if (
      props.currentUser &&
      props.supportInfo &&
      !prevProps.supportInfo &&
      props.currentUser === prevProps.currentUser
    ) {
      activatePersistedPylonChat(
        props.currentUser.name,
        props.currentUser.email,
        props.supportInfo.pylonAppId,
        props.supportInfo.pylonEmailHash,
      );
    }

    this.props = props;
    this.updateComponents();
  }

  mixpanelTrack(event: TrackingEvent, properties: {[key: string]: any} = {}) {
    const commonProperties = {
      tab: getReportName(),
      'Is Collapsed': this.isCollapsed(),
      'Expansion Mode': this.getExpansionMode(),
    };

    mixpanel.track(`[Global Sidenav] ${event}`, {
      ...commonProperties,
      ...properties,
    });
  }

  sendbirdTrack(event: SendbirdTrackingEvent) {
    const commonProperties = {
      tab: getReportName(),
    };

    mixpanel.track(`[Sendbird Help Chat] ${event}`, {
      ...commonProperties,
    });
  }

  updateState(stateUpdate: Partial<State>) {
    this.state = Object.assign({}, this.state, stateUpdate);
    this.updateComponents();
  }

  getUserLocalStorageHelper(): UserLocalStorageHelper<PersistedState> {
    return new UserLocalStorageHelper<PersistedState>({
      context: this.props.localStorageManager,
      namespace: LocalStorageKey.GlobalSidenav,
    });
  }

  updatePersistedState(persistedStateUpdate: Partial<PersistedState>) {
    const userId = userCookieUtils.getUserIdentityFromCookie()?.id;
    if (!userId) {
      return;
    }
    const oldPersistedState = this.getPersistedState();
    this.getUserLocalStorageHelper().setItem({
      userId,
      key: PersistedStateKey,
      value: {...oldPersistedState, ...persistedStateUpdate},
    });
    this.updateComponents();
  }

  getPersistedState(): PersistedState | undefined {
    const userId = userCookieUtils.getUserIdentityFromCookie()?.id;
    if (!this.props || !userId) {
      return undefined;
    }
    return this.getUserLocalStorageHelper().getItem({key: PersistedStateKey, userId}) ?? undefined;
  }

  isNarrowWidthReport(): boolean {
    return [ReportName.Lexicon, ReportName.Settings, ReportName.AdminOrgSettings].includes(getReportName());
  }

  getExpansionMode(): ExpansionMode {
    return this.getPersistedState()?.expansionMode ?? ExpansionMode.Auto;
  }

  getIsSparkOpen(): boolean {
    return (
      (this.props &&
        this.getPersistedState()?.isSparkOpen &&
        isAIEnabled(this.props.currentProject) &&
        isSparkCopilotEnabled(this.props.currentProject)) ??
      false
    );
  }

  isCollapsed(): boolean {
    const expansionMode = this.getExpansionMode();
    if (expansionMode === ExpansionMode.Auto) {
      return this.isNarrowWidthReport();
    }
    return expansionMode === ExpansionMode.AlwaysCollapsed;
  }

  updateExpansionMode({
    isCollapsed,
    expansionSource,
  }: {
    isCollapsed: boolean;
    expansionSource: ExpansionSource | undefined;
  }): void {
    let newExpansionMode: ExpansionMode;
    if (isCollapsed && !this.isNarrowWidthReport()) {
      newExpansionMode = ExpansionMode.AlwaysCollapsed;
    } else if (!isCollapsed && this.isNarrowWidthReport()) {
      newExpansionMode = ExpansionMode.AlwaysExpanded;
    } else {
      newExpansionMode = ExpansionMode.Auto;
    }
    this.setExpansionMode({expansionMode: newExpansionMode, expansionSource});
  }

  setExpansionMode({
    expansionMode,
    expansionSource,
  }: {
    expansionMode: ExpansionMode;
    expansionSource: ExpansionSource | undefined;
  }): void {
    const oldExpansionMode = this.getExpansionMode();
    if (expansionMode !== oldExpansionMode) {
      this.updatePersistedState({expansionMode});
      this.mixpanelTrack(TrackingEvent.ExpansionToggled, {
        'Old Expansion Mode': oldExpansionMode,
        'New Expansion Mode': expansionMode,
        'Expansion Source': expansionSource,
      });
    }
  }

  /**
   * Collapse the left nav when opening a report from the agent chat so the
   * report has room alongside the Spark panel. Forces AlwaysCollapsed rather
   * than Auto so a narrow-width report can't resolve back to expanded.
   */
  collapseOnReportOpen(): void {
    this.setExpansionMode({
      expansionMode: ExpansionMode.AlwaysCollapsed,
      expansionSource: ExpansionTrigger.SparkReportOpened,
    });
  }

  hideCustomerSupportWidgets() {
    this.hideSendbirdWidget();
    this.hideFrigadeAssistant();
    hidePylonWidget();
  }

  hideSendbirdWidget() {
    if (this.state.showSendbirdWidget) {
      this.sendbirdTrack(SendbirdTrackingEvent.ChatClosed);
    }
    if (this.props.currentUser) {
      const userLocalStorageHelper = new UserLocalStorageHelper({
        context: this.props.localStorageManager,
        namespace: LocalStorageKey.SendbirdHelpChatOpened,
        ttlMs: SendbirdLocalStorageTTL,
      });

      userLocalStorageHelper.setItem({
        key: LocalStorageKey.SendbirdHelpChatOpened,
        userId: this.props.currentUser.id,
        value: false,
      });
    }
    this.updateState({showSendbirdWidget: false});
  }

  openContactSupport() {
    // Contact Support is the single Pylon entry point. Bail BEFORE hiding
    // existing widgets if supportInfo isn't loaded yet so the button isn't
    // a silent no-op when the bootstrap data hasn't arrived or has 5xx'd.
    if (!this.props.currentUser || !this.props.supportInfo) {
      return;
    }
    this.hideCustomerSupportWidgets();
    activatePylonChat(
      this.props.currentUser.name,
      this.props.currentUser.email,
      this.props.supportInfo.pylonAppId,
      this.props.supportInfo.pylonEmailHash,
    );
    trackPylonLiveChatStarted();
  }

  openSendbirdChat() {
    this.sendbirdTrack(SendbirdTrackingEvent.ChatOpened);
    this.hideCustomerSupportWidgets();
    this.updateState({
      showSendbirdWidget: true,
    });
  }

  openFrigadeAssistant({isReopenAfterPageLoad = false}: {isReopenAfterPageLoad?: boolean} = {}) {
    mixpanel.track(`[Frigade Assistant] Chat Opened`, {
      tab: getReportName(),
      'reopened after page load': isReopenAfterPageLoad,
    });
    // Re-opening an assistant that is already open must not route through
    // `hideCustomerSupportWidgets()`. That hides the vendor widget imperatively
    // and drops `showFrigadeWidget` to false, but React batches both updates
    // into one render that only ever sees the final `true` - so the footer
    // effect's deps never change, it never re-runs, and nothing undoes the
    // `hide()`. The vendor is already configured, so ask it to show directly.
    if (this.state.showFrigadeWidget) {
      showFrigadeAssistantWidget();
      return;
    }
    this.hideCustomerSupportWidgets();
    this.updateState({
      showFrigadeWidget: true,
    });
  }

  hideFrigadeAssistant() {
    hideFrigadeAssistantWidget();
    this.updateState({showFrigadeWidget: false});
  }

  toggleSpark(opts: {focusInput?: boolean} = {}) {
    this.setSparkOpen(!this.getIsSparkOpen(), opts);
  }

  setSparkOpen(isOpen: boolean, opts: {silent?: boolean; focusInput?: boolean} = {}) {
    const wasOpen = this.getIsSparkOpen();
    this.updateState({focusInputOnOpen: isOpen && Boolean(opts.focusInput)});
    if (isOpen === wasOpen) {
      return;
    }
    this.updatePersistedState({isSparkOpen: isOpen});

    if (opts.silent) {
      return;
    }

    if (isOpen) {
      trackSparkOpened();
    } else {
      trackSparkClosed();
    }
  }

  /**
   * Opens the global search modal, which `GlobalSidenav` renders. No-ops
   * outside a project since the modal needs one.
   */
  openSearchModal({keyboardShortcut}: {keyboardShortcut?: string} = {}): void {
    if (!this.props.currentProject || this.state.isSearchModalOpen) {
      return;
    }
    this.mixpanelTrack(TrackingEvent.SearchModalOpened, {
      'Keyboard Shortcut': keyboardShortcut,
    });
    this.updateState({isSearchModalOpen: true});
  }

  closeSearchModal(): void {
    if (this.state.isSearchModalOpen) {
      this.updateState({isSearchModalOpen: false});
    }
  }

  toggleSearchModal(opts: {keyboardShortcut?: string} = {}): void {
    if (this.state.isSearchModalOpen) {
      this.closeSearchModal();
    } else {
      this.openSearchModal(opts);
    }
  }

  getInviteUserOrgs(): Organization[] {
    const userOrganizations = this.props.currentUser?.organizations ?? {};
    const orgUrlMatch = /^\/settings\/org\/(\d+)/.exec(location.pathname);
    const orgId = (orgUrlMatch && parseInt(orgUrlMatch[1], 10)) || null;
    const orgs = getFilteredOrgs(userOrganizations, {permissions: [Permission.AddUser]});
    const org = orgs.find((o) => o.id === orgId);
    // if org id is specified by url and that org has `add_user` permission, show
    // invite modal for that org; otherwise show modal for all orgs with `add_user`
    // (user will choose org in modal)
    return org ? [org] : orgs;
  }

  openInviteUserModal({closeCallback}: {closeCallback?: () => void} = {}): void {
    const inviteUserOrgs = this.getInviteUserOrgs();
    if (!inviteUserOrgs.length) {
      return;
    }

    this.updateState({
      isInviteUserModalOpen: true,
      closeInviteModalCallback: closeCallback,
    });
  }

  closeInviteModal(): void {
    this.state.closeInviteModalCallback?.();
    this.updateState({
      isInviteUserModalOpen: false,
      closeInviteModalCallback: undefined,
    });
  }

  getWorkspaceBasedAppUrl(appName: string): string {
    const currentProject = this.props.currentProject;
    const currentWorkspaceId = getCurrentWorkspaceId({currentProject});
    // If there's no workspace in the current URL, find a valid one to use
    const workspaceId =
      currentWorkspaceId ?? findCurrentValidWorkspaceId({currentProject, firstPriorityWorkspaceId: currentWorkspaceId});
    return getReportUrl(appName, {
      projectId: currentProject?.id ?? getProjectId(),
      workspaceId,
    });
  }

  isSectionExpanded(sectionKey: SectionKey): boolean {
    return this.getPersistedState()?.expandedSections?.[sectionKey] ?? false;
  }

  updateSectionExpansionState(sectionKey: SectionKey, isExpanded: boolean): void {
    const currentSectionStates = this.getPersistedState()?.expandedSections || {};

    this.updatePersistedState({
      expandedSections: {
        ...currentSectionStates,
        [sectionKey]: isExpanded,
      },
    });

    // Track the expansion change
    this.mixpanelTrack(TrackingEvent.SectionExpansionToggled, {
      'Section Key': sectionKey,
      'Is Expanded': isExpanded,
    });
  }

  subscribe(callback: () => void): this {
    this.subscriptions.push(callback);
    return this;
  }

  unsubscribe(callback: () => void): this {
    this.subscriptions = this.subscriptions.filter((cb) => cb !== callback);
    return this;
  }

  bindToComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.add(component);
  }

  unbindFromComponent(component: Component<any, any, any, any, any>): void {
    this.connectedComponents.delete(component);
  }

  updateComponents() {
    for (const component of this.connectedComponents) {
      component.update();
    }
    for (const subscription of this.subscriptions) {
      subscription();
    }
  }
}
